import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { faqKeys } from "@/features/faq/hooks/use-faq";
import { servicesKeys } from "@/features/services/hooks/use-services";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { EnrichmentAPI } from "../api/enrichment";
import type {
  EnrichmentRun,
  EnrichmentScope,
  EnrichmentState,
  ProposalSelection,
} from "../types";

export const enrichmentKeys = {
  all: ["enrichment"] as const,
  state: (clientId: string) => [...enrichmentKeys.all, clientId] as const,
};

const useClientId = () => useCurrentClient()?.client?.id || "";

/**
 * The fast track's server state: the last run of each scope.
 *
 * Polls only while a run is in flight. The Add dialog that starts a run and the
 * page that reviews its proposal read the same query, which is what lets the
 * dialog close on « Remplir » and the page pick the reading up.
 */
export function useEnrichmentState() {
  const clientId = useClientId();

  return useQuery<EnrichmentState>({
    queryKey: enrichmentKeys.state(clientId),
    queryFn: () => EnrichmentAPI.getState(clientId),
    enabled: !!clientId,
    staleTime: 10 * 1000,
    refetchInterval: (query) =>
      Object.values(query.state.data?.runs || {}).some(
        (run) => run?.status === "running",
      )
        ? 3_000
        : false,
  });
}

/** Put a run back into the cached state under each scope it covers. */
const useStoreRun = () => {
  const clientId = useClientId();
  const queryClient = useQueryClient();

  return (run: EnrichmentRun) =>
    queryClient.setQueryData<EnrichmentState>(
      enrichmentKeys.state(clientId),
      (previous) => ({
        website: previous?.website || run.url,
        scopes: previous?.scopes || run.scopes,
        runs: {
          ...(previous?.runs || {}),
          ...Object.fromEntries(run.scopes.map((scope) => [scope, run])),
        },
      }),
    );
};

export function useStartEnrichment() {
  const clientId = useClientId();
  const storeRun = useStoreRun();

  return useMutation({
    mutationFn: (input: { scopes: EnrichmentScope[]; url?: string }) =>
      EnrichmentAPI.start(clientId, input),
    onSuccess: storeRun,
  });
}

/**
 * Write the ticked part of a proposal. This is the one moment the fast track
 * touches `services` / `faqs`, so it is the one that refreshes them.
 */
export function useApplyEnrichment() {
  const clientId = useClientId();
  const queryClient = useQueryClient();
  const storeRun = useStoreRun();

  return useMutation({
    mutationFn: (input: {
      runId: string;
      scope: EnrichmentScope;
      items: ProposalSelection[];
    }) =>
      EnrichmentAPI.apply(clientId, input.runId, {
        scope: input.scope,
        items: input.items,
      }),
    onSuccess: (run, input) => {
      storeRun(run);
      queryClient.invalidateQueries({
        queryKey: input.scope === "faq" ? faqKeys.all : servicesKeys.all,
      });
    },
    // A refusal means the page showed something the server no longer holds
    // (another tab applied it, a newer reading replaced it): show what is.
    onError: () =>
      queryClient.invalidateQueries({
        queryKey: enrichmentKeys.state(clientId),
      }),
  });
}

export function useDismissEnrichment() {
  const clientId = useClientId();
  const queryClient = useQueryClient();
  const storeRun = useStoreRun();

  return useMutation({
    mutationFn: (input: { runId: string; scope: EnrichmentScope }) =>
      EnrichmentAPI.dismiss(clientId, input.runId, { scope: input.scope }),
    onSuccess: storeRun,
    onError: () =>
      queryClient.invalidateQueries({
        queryKey: enrichmentKeys.state(clientId),
      }),
  });
}

/**
 * Fire `onFinished` when a reading this page watched running comes to an end.
 *
 * Only a transition seen from `running` counts: a run that finished last week
 * is already on screen and has nothing to announce.
 */
export function useEnrichmentFinished(
  run: EnrichmentRun | null | undefined,
  onFinished: (run: EnrichmentRun) => void,
) {
  const watched = useRef<string | null>(null);

  useEffect(() => {
    if (!run) return;
    if (run.status === "running") {
      watched.current = run.id;
      return;
    }
    if (watched.current !== run.id) return;
    watched.current = null;
    // `onFinished` is deliberately left out of the dependencies: callers pass
    // an inline closure, and the ref is the real guard.
    onFinished(run);
  }, [run]);
}
